<script>
  import { IconClose, IconSearch } from "@urbaninstitute/dataviz-components/icons";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import { GLOSSARY_POPOVER_HEADING } from "$utils/constants.js";
  let { glossary_data, onclose } = $props();

  const COLUMN_ORDER = [
    'BOUNDARY POLICY TRANSPARENCY',
    'CHARTER SCHOOLS',
    'MAGNET SCHOOLS',
    'SEGREGATION',
    'SPECIALIZED SCHOOLS',
    'STATE POLICIES ON OPEN ENROLLMENT',
    'INTERDISTRICT OPEN ENROLLMENT',
    'INTRADISTRICT OPEN ENROLLMENT',
    'TRANSPORTATION TO SCHOOL CHOICE',
  ];

  const byTitle = Object.fromEntries(
    Object.values(glossary_data).map(item => [item.title.trim().toUpperCase(), item])
  );

  const orderedItems = COLUMN_ORDER.map(t => byTitle[t]).filter(Boolean);
</script>

{#snippet glossaryItem(item)}
  <div class="glossary-item">
    {#if item.type === "basic"}
      <p class="item-header">{item.title}</p>
      {#each item.text as t}
        <p class="item-text">{@html t.value}</p>
      {/each}
    {:else if item.type === "transport"}
      <p class="item-header">{item.title}</p>
      {#if item.first_graf}<p class="item-text">{@html item.first_graf}</p>{/if}
      {#if item.bullet_intro}<p class="item-text">{@html item.bullet_intro}</p>{/if}
      {#if item.bullets}
        <ul class="item-bullets">
          {#each item.bullets as bullet}
            <li class="item-text">{@html bullet.value}</li>
          {/each}
        </ul>
      {/if}
    {:else}
      <p class="item-header">{item.title}</p>
      {#if item.text}
        {#each item.text as t}
          <p class="item-text">{@html t.value}</p>
        {/each}
      {:else}
        <p class="item-text">{@html item.first_bullet_intro}</p>
        <div class="block-quote">
          {#each item.first_bullets as bullet}
            <p class="item-text">{@html bullet.value}</p>
          {/each}
        </div>
        <p class="item-text">{@html item.second_bullet_intro}</p>
        <div class="block-quote">
          {#each item.second_bullets as bullet}
            <p class="item-text">{@html bullet.value}</p>
          {/each}
        </div>
        <p class="item-text">{@html item.third_bullet_intro}</p>
        <div class="block-quote">
          {#each item.third_bullets as bullet}
            <p class="item-text">{@html bullet.value}</p>
          {/each}
        </div>
      {/if}
    {/if}
  </div>
{/snippet}

<div class="glossary-background">
  <div class="glossary-page">
    <div class="close-button">
      <div class="btn icon-btn" role="button" tabindex="0" onclick={onclose} onkeydown={onclose}>
        <IconClose fill={urbanColors.white} size={16} />
      </div>
    </div>
    <div class="glossary-content">
      <div class="glossary-heading">
        <IconSearch size={16} />
        <h6>{GLOSSARY_POPOVER_HEADING}</h6>
      </div>
      <div class="glossary-columns">
        {#each orderedItems as item}
          {@render glossaryItem(item)}
        {/each}
      </div>
    </div>
  </div>
</div>

<style>
  .glossary-background {
    display: block;
    position: absolute;
    z-index: 501;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
  }
  .glossary-page {
    padding: 75px;
    display: flex;
    flex-direction: column;
    position: absolute;
    z-index: 501;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
    background-color: var(--neutral-lightest, #F5F5F5);
    border-radius: 45px;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
    overflow-y: auto;
    box-sizing: border-box;
    container-type: inline-size;
  }
  .glossary-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
  /* Mobile-first: a single, normal-flow column that grows with its
     content. The popup (.glossary-page) scrolls, so on small screens the
     glossary simply extends downward instead of being squeezed into
     columns. */
  .glossary-columns {
    column-gap: 40px;
  }
  .glossary-columns .glossary-item {
    break-inside: avoid;
  }
  /* Desktop two-page spread: fill three columns, column-by-column. The
     fixed height (flex: 1) is what makes column-fill: auto pack column 1
     full before moving on, so it is scoped to this tier only. */
  @media (min-width: 1160px) {
    .glossary-columns {
      flex: 1;
      min-height: 0;
      columns: 3;
      column-fill: auto;
    }
  }
  /* Mobile / tablet: drop the fixed-height overlay so the glossary grows
     to fit its content in normal flow and extends the layout downward
     instead of scrolling inside itself. */
  /* Mobile + tablet: darken the notebook behind the glossary (lightbox,
     rounded to match the notebook) and keep the panel to its natural
     height, capped at the notebook so it never extends past the normal
     bottom. If the data viz peeks out underneath, that is fine — it shows
     through the dimmed backdrop. */
  @media (max-width: 1159px) {
    .glossary-background {
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 45px;
    }
    .glossary-page {
      position: relative;
      top: auto;
      bottom: auto;
      height: auto;
      max-height: 100%;
      overflow-y: auto;
    }
  }
  /* Mobile: smaller padding to reclaim space on narrow screens. */
  @media (max-width: 799px) {
    .glossary-page {
      padding: 30px;
    }
    .close-button {
      top: 30px;
      right: 30px;
    }
  }

  .glossary-nav {
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;
  }
  .btn {
    display: inline-flex;
    gap: var(--spacing-2);
    padding: var(--spacing-2);
    cursor: pointer;
    background: black;
    color: white;
    border: solid 2px var(--color-black);
    border-radius: 20px;
    justify-content: space-between;
    align-items: center;
    text-align: end;
    font-weight: var(--font-weight-bold);
  }
  button.btn {
    font-family: inherit;
    font-size: inherit;
  }
  .nav-btn {
    padding: var(--spacing-2) var(--spacing-4);
  }
  .close-button {
    display: flex;
    justify-content: end;
    position: absolute;
    top: 45px;
    right: 45px;
    .btn {
      &:focus,
      &:hover {
        outline: solid 2px var(--color-blue);
      }
    }
  }
  .glossary-content .glossary-heading {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 16px;
    h6 {
      font-family: var(--font-family-sans-alt);
      font-size: 20px;
      font-weight: var(--font-weight-bold);
      line-height: 24px;
    }
  }
  .glossary-item {
    margin-bottom: 30px;
  }
  .glossary-item p.item-header {
    color: var(--color-gray-shade-darkest);
    font-family: var(--font-family-sans-alt);
    font-size: 14px;
    font-weight: var(--font-weight-bold);
    line-height: 20px;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    margin: 0 0 4px;
  }
  .glossary-item p.item-text {
    font-size: 16px;
    line-height: 150%;
    margin: 0 0 10px;
  }
  .glossary-item ul.item-bullets {
    margin: 0 0 10px;
    padding-left: 20px;
    list-style: disc;
  }
  .glossary-item ul.item-bullets li.item-text {
    font-size: 16px;
    line-height: 150%;
    margin: 0 0 4px;
  }
  .glossary-item .block-quote {
    margin-left: 14px;
    margin-top: 10px;
    margin-bottom: 10px;
    padding-left: 14px;
    border-left: 3px solid var(--color-gray-shade-light);
  }
</style>
